import React, { useState, useRef, useMemo, useEffect, useCallback, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toAbsoluteUrl } from '../lib/media';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useLowBandwidth } from '../contexts/LowBandwidthContext';
import { toast } from 'sonner';
import ConfirmDialog from '../components/ConfirmDialog';
import ProfileTypePicker, { 
  getStoredProfileType, 
  getProfileRoute,
  PROFILE_TYPES 
} from '../components/ProfileTypePicker';
import { usePostAdEntry } from '../hooks/usePostAdEntry';
import { canBoostAd } from '../components/AvailabilityBadge';
import { getApiBase } from '../api/httpClient';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import SafetyTipsCard from '../components/SafetyTipsCard';
import { buildShareUrl, shareNative } from '../utils/shareUtils';
import InlineError from '../components/InlineError';
import '../components/brand.css';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  User,
  Pencil,
  Moon,
  ShieldCheck,
  FileText,
  Mail,
  Trash2,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Camera,
  GraduationCap,
  Briefcase,
  Store,
  ShoppingBag,
  Wrench,
  UserCircle,
  Settings,
  X,
  Check,
  Loader2,
  ChevronsUpDown,
  Globe,
  Menu,
  Bell,
  Bookmark,
  Wallet,
  TrendingUp,
  Eye,
  Package,
  MessageSquare,
  Link2,
  Activity,
  BarChart3,
  Languages,
  Plus,
  Zap,
  Pause,
  Play,
  Clock,
  AlertCircle,
  CheckCircle,
  Facebook,
  Sparkles,
  CreditCard,
  Phone,
  Edit2,
  Undo2,
  Wifi,
  Shield,
  PieChart,
  Users,
  RefreshCw,
  Heart,
  Info,
  EyeOff,
  Send,
  Tag
} from 'lucide-react';
import RoleSelection from '../components/RoleSelection';
import AccountTopHeader from '../components/AccountTopHeader';
import BoostSelectionModal from '../components/BoostSelectionModal';
import AboutWasslaModal from '../components/AboutWasslaModal';
import WasslaLogo from '../components/WasslaLogo';
import { BRAND } from '../constants/brand';
import { useAppVersion } from '../version';
import { COUNTRIES } from '../data/countries';
import { normalizePhoneForCountry, isValidMessenger } from '../utils/phone';
import { useFavorites } from '../contexts/FavoritesContext';
import { updateAnalyticsPreference, getAnalyticsPreference } from '../utils/helpAnalytics';
import { normalizeStatus, getStatusBadge, MY_ADS_TABS, filterAdsByTab, countAdsByTab, getCardActions } from '../utils/adStatus';
import { trackEvent } from '../utils/analytics';
import { useNavGuard } from '../contexts/NavGuardContext';
import { COINS_DISABLED } from '../config/coinsFlag';

// Settings "About" footer version — read live from the installed package.
const AboutVersionLabel = ({ isRTL }) => {
  const { version, build } = useAppVersion();
  return (
    <div className="app-about-footer-version" data-testid="about-version-label">
      {version
        ? (isRTL ? `وصلة ${version} (${build})` : `Wassla ${version} (${build})`)
        : (isRTL ? 'وصلة ويب' : 'Wassla Web')}
    </div>
  );
};
import './Account.css';
import '../components/AboutWasslaModal.css';

// Role icon mapping
const ROLE_ICONS = {
  seller: Store,
  buyer: ShoppingBag,
  student: GraduationCap,
  service: Wrench,
  professional: Briefcase,
  general: UserCircle,
  // Backward compat for old DB values
  trader: Store,
  service_provider: Wrench,
  individual: UserCircle,
};

// ========================================
// STABLE FormRow component - DEFINED OUTSIDE to prevent remounting
// ========================================
const FormRow = memo(function FormRow({ label, hint, children, isRTL }) {
  return (
    <div className="flex flex-col gap-2 w-full max-w-full min-w-0 mb-4">
      <label className={`text-sm font-semibold text-neutral-700 dark:text-neutral-300 ${isRTL ? 'text-right' : 'text-left'}`}>
        {label}
      </label>
      <div className="w-full min-w-0">
        {children}
      </div>
      {hint && (
        <span className={`text-xs text-neutral-500 dark:text-neutral-400 ${isRTL ? 'text-right' : 'text-left'}`}>
          {hint}
        </span>
      )}
    </div>
  );
});

// ========================================
// Stable EditInput component - prevents remounting on every keystroke
// ========================================
const EditInput = memo(function EditInput({ 
  name,
  value, 
  onChange, 
  placeholder, 
  type = 'text',
  isRTL,
  testId
}) {
  return (
    <input
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      className={`w-full h-12 rounded-xl border border-neutral-200 dark:border-neutral-600 px-4 outline-none focus:ring-2 focus:ring-green-200 focus:border-green-500 dark:bg-slate-800 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}
      data-testid={testId}
      autoCorrect="off"
      autoCapitalize="none"
      autoComplete="off"
      enterKeyHint="done"
    />
  );
});

function Account() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { favAds } = useFavorites();
  const { user, token, logout, updateUser } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { lang, toggleLanguage } = useLanguage();
  const language = i18n.language || 'ar';
  const isRTL = language.startsWith('ar');
  const isArabic = isRTL; // Alias for consistency across the codebase
  
  // Low Bandwidth Mode from context
  const { isLowBandwidth, toggleLowBandwidth } = useLowBandwidth();
  const { triggerPostAd, DraftResumeModal } = usePostAdEntry();
  
  // Check if coming from coins page to edit profile
  const fromCoins = searchParams.get('from') === 'coins';
  const shouldEdit = searchParams.get('edit') === 'true';
  
  // Menu & Dialog states
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [footerLogoError, setFooterLogoError] = useState(false);
  
  // Change password states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Update email states (NEW)
  const [showUpdateEmail, setShowUpdateEmail] = useState(false);
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    currentPassword: '',
    newEmail: ''
  });
  
  // Clear cache & notification settings states (NEW)
  const [showClearCacheDialog, setShowClearCacheDialog] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    push_enabled: true,
    email_enabled: true,
    messages: true,
    listing_activity: true,
    saved_searches: true
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  
  // About Wassla Modal state
  const [showAboutModal, setShowAboutModal] = useState(false);
  
  // Get auth methods for profile type
  const { setProfileType: authSetProfileType } = useAuth();
  
  // Profile Type Picker state (Settings)
  const [showProfileTypePicker, setShowProfileTypePicker] = useState(false);
  const [currentProfileType, setCurrentProfileType] = useState(() => getStoredProfileType());
  
  // Boost Modal state
  const [boostModalOpen, setBoostModalOpen] = useState(false);
  const [selectedAdForBoost, setSelectedAdForBoost] = useState(null);
  
  // Favorites tab state
  const [myAdsTab, setMyAdsTab] = useState('my_ads'); // 'my_ads' | 'favorites'
  const [myAdsSubTab, setMyAdsSubTab] = useState('active'); // active | draft | moderation | hidden | sold
  const [favorites, setFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  
  // Handle profile type selection from settings - with backend persistence
  const handleProfileTypeSelect = async (type) => {
    // Save to backend via AuthContext
    const success = await authSetProfileType(type);
    
    if (success) {
      setCurrentProfileType(type);
      
      // Also cache locally
      localStorage.setItem('wassla_profile_type', type);
      localStorage.setItem('wassla_profile_type_set_at', new Date().toISOString());
      localStorage.setItem('hasCompletedProfileSelection', 'true');
      
      const targetRoute = getProfileRoute(type, true);
      
      toast.success(
        t('acco.yourProfileTypeHasBeen'),
        { duration: 3000 }
      );
      
      setShowProfileTypePicker(false);
      
      if (targetRoute && targetRoute !== '/account') {
        setTimeout(() => {
          navigate(targetRoute);
        }, 500);
      }
    } else {
      toast.error(
        t('acco.failedToUpdateProfileType')
      );
    }
  };
  
  // Analytics opt-out state
  const [analyticsOptOut, setAnalyticsOptOut] = useState(false);
  
  // Load analytics preference on mount
  useEffect(() => {
    const loadAnalyticsPreference = async () => {
      const optOut = await getAnalyticsPreference();
      setAnalyticsOptOut(optOut);
    };
    loadAnalyticsPreference();
  }, []);
  
  // Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    whatsapp_phone: '',
    messenger_link: '',
    country_code: 'SD',
    region: '',
    city: ''
  });
  const [countryOpen, setCountryOpen] = useState(false);
  const [countryConfirm, setCountryConfirm] = useState({ open: false, code: null });
  
  // Photo upload states
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Copy wallet state
  const [walletCopied, setWalletCopied] = useState(false);
  
  // Dashboard data states
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [userAds, setUserAds] = useState([]);
  const [stats, setStats] = useState({
    activeAds: 0,
    totalViews: 0,
    walletBalance: 0,
    responses: 0,
    viewsToday: 0,
    interactionsWeek: 0
  });
  const [topAd, setTopAd] = useState(null);
  const [analyticsReady, setAnalyticsReady] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [messageThreadCount, setMessageThreadCount] = useState(0);

  // Social connections state
  const [socialStatus, setSocialStatus] = useState({
    facebook: { connected: false, name: null },
    instagram: { connected: false, username: null }
  });
  const [connectingFacebook, setConnectingFacebook] = useState(false);
  const [connectingInstagram, setConnectingInstagram] = useState(false);
  
  // Social link modal state
  const [socialLinkModal, setSocialLinkModal] = useState({
    open: false,
    provider: null, // 'facebook' or 'instagram'
    inputValue: '',
    error: null,
    saving: false
  });

  // Unlink confirmation modal state
  const [confirmUnlink, setConfirmUnlink] = useState({
    open: false,
    provider: null, // 'facebook' or 'instagram'
    unlinking: false
  });

  // Auto-enable edit mode if coming from coins page
  useEffect(() => {
    if (shouldEdit && !isEditing) {
      // Seed the form from the saved profile (pre-fill) — same as the
      // menu path — so the ?edit=true deep link never opens a blank form.
      handleStartEdit();
      // Clear the query params after enabling edit mode
      setSearchParams({});
      // Show info toast if from coins page
      if (fromCoins) {
        toast.info(
          t('acco.completeYourProfileNameAddress')
        );
      }
      // iter340 — Once edit mode has opened and React has painted the
      // editable form, smooth-scroll the Name input into view and
      // focus it so the seller lands DIRECTLY on the first editable
      // field. Fixes the operator's complaint that "tapping Complete
      // your profile only opens My Account — user still has to hunt
      // for where to edit". Uses a rAF to wait for the form nodes
      // to mount, no dependency on internal Account layout.
      requestAnimationFrame(() => {
        setTimeout(() => {
          const target = document.querySelector('[data-testid="name-input"]')
            || document.querySelector('[data-testid="account-name-input"]')
            || document.querySelector('.account-page input[type="text"]');
          if (!target) return;
          try {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch { /* noop */ }
          setTimeout(() => {
            try { target.focus({ preventScroll: true }); } catch { /* noop */ }
          }, 320);
        }, 60);
      });
    }
  }, [shouldEdit, fromCoins, isRTL]);

  // iter367 — notification preferences live on the user account (backend),
  // not in localStorage. Loaded fresh every time the modal opens.
  useEffect(() => {
    if (!showNotificationSettings || !token) return;
    (async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/users/me/notification-preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        const p = data.preferences || data || {};
        setNotificationPrefs({
          push_enabled: p.push_enabled !== false,
          email_enabled: p.email_enabled !== false,
          messages: p.message_notifications !== false,
          listing_activity: p.ad_performance !== false || p.new_ads_alerts !== false,
          saved_searches: p.saved_search_alerts !== false,
        });
      } catch { /* keep defaults on failure */ }
    })();
  }, [showNotificationSettings, token]);

  const handleSaveNotificationPrefs = async () => {
    if (savingPrefs) return;
    setSavingPrefs(true);
    try {
      const res = await fetch(`${getApiBase()}/api/users/me/notification-preferences`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          push_enabled: notificationPrefs.push_enabled,
          email_enabled: notificationPrefs.email_enabled,
          message_notifications: notificationPrefs.messages,
          ad_performance: notificationPrefs.listing_activity,
          new_ads_alerts: notificationPrefs.listing_activity,
          saved_search_alerts: notificationPrefs.saved_searches,
        })
      });
      if (!res.ok) throw new Error('save_failed');
      toast.success(t('account.notifPrefsUpdated'));
      setShowNotificationSettings(false);
    } catch {
      toast.error(t('account.notifPrefsError'));
    } finally {
      setSavingPrefs(false);
    }
  };

  // Fetch dashboard data
  useEffect(() => {
    if (token) {
      fetchDashboardData();
    }
  }, [token]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // Fetch user's ads, canonical seller analytics, unified seller stats, AND message threads in parallel.
      // iter345 — /api/users/me/analytics/overview is the single source
      // of truth for Total Views / views_today / views_this_week / top
      // performing ad / active_ads_count / wallet_balance / responses.
      const [adsResponse, overviewResponse, sellerStatsResponse, threadsResponse] = await Promise.all([
        fetchWithTimeout(`${process.env.REACT_APP_BACKEND_URL}/api/ads/user/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetchWithTimeout(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/analytics/overview`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null),
        user?.user_id
          ? fetchWithTimeout(`${process.env.REACT_APP_BACKEND_URL}/api/seller/${user.user_id}/stats`).catch(() => null)
          : Promise.resolve(null),
        fetchWithTimeout(`${process.env.REACT_APP_BACKEND_URL}/api/messages/threads`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null),
      ]);
      
      let ads = [];
      let backendAdCounts = null;
      if (adsResponse.ok) {
        const adsData = await adsResponse.json();
        ads = adsData.ads || [];
        backendAdCounts = adsData.counts || null;
        setUserAds(ads);
      }

      // Get accurate overview stats from backend
      let overviewStats = null;
      if (overviewResponse?.ok) {
        overviewStats = await overviewResponse.json();
        setAnalyticsReady(true);
      }
      
      // Get unified seller stats — SINGLE SOURCE OF TRUTH for counts
      let unifiedStats = null;
      if (sellerStatsResponse?.ok) {
        unifiedStats = await sellerStatsResponse.json();
      }

      // Calculate stats using canonical ad status system.
      // Bug fix 2026-06-01h: tab/total counts must come from backend
      // `data.counts` block, NOT from countAdsByTab(ads) — the paginated
      // `ads` list caps at 100 + 50 books, so a power user / admin gets
      // drift between Account overview and My Ads. The backend counts use
      // get_seller_stats() (uncapped) — the same numbers used by
      // /api/seller/<id>/stats, Seller Profile, and Ad Detail.
      const tabCounts = countAdsByTab(ads);
      const computedStats = {
        activeAds: backendAdCounts?.active ?? tabCounts.active ?? 0,
        totalAds: backendAdCounts?.all ?? ads.length,
        // iter345 — reads canonical analytics fields.
        totalViews: overviewStats?.total_views || 0,
        todayViews: overviewStats?.views_today || 0,
        weekViews: overviewStats?.views_this_week || 0,
        coins: user?.coins || 0,
        draftAds: backendAdCounts?.draft ?? tabCounts.draft ?? 0,
        soldAds: backendAdCounts?.sold ?? tabCounts.sold ?? 0,
        hiddenAds: backendAdCounts?.hidden ?? tabCounts.hidden ?? 0,
        moderationAds: backendAdCounts?.pending ?? tabCounts.moderation ?? 0,
      };
      
      // Override activeAds with unified stats (single source of truth for published count)
      // NOTE: Do NOT override totalViews here — canonical analytics
      // overview is authoritative.
      if (unifiedStats) {
        computedStats.activeAds = unifiedStats.published_ads_count;
      }
      
      // iter345 — Top performing ad now comes from the same canonical
      // analytics response so Overview + Performance Insights + Views
      // page always show the same ad + view count.
      if (overviewStats?.top_performing_ad) {
        const top = overviewStats.top_performing_ad;
        // Enrich with data from the ads list if available (image, price, etc.)
        const enriched = ads.find(a => a.ad_id === top.ad_id) || {};
        setTopAd({ ...enriched, ...top, viewCount: top.views });
      } else {
        setTopAd(null);
      }

      setStats(computedStats);

      // Get message thread count — same source as Messages page (/api/messages/threads)
      if (threadsResponse?.ok) {
        try {
          const threadsData = await threadsResponse.json();
          const threadCount = (threadsData.threads || []).length;
          setMessageThreadCount(threadCount);
        } catch {}
      }

    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch social connections status
  const fetchSocialStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSocialStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch social status:', err);
    }
  };

  // Fetch social status on mount
  useEffect(() => {
    if (token) {
      fetchSocialStatus();
    }
  }, [token]);

  // Handle Facebook connect/disconnect
  const handleFacebookConnect = async () => {
    if (socialStatus.facebook.connected) {
      // Show confirmation dialog before disconnecting
      setConfirmUnlink({ open: true, provider: 'facebook', unlinking: false });
    } else {
      // Open modal to enter Facebook profile
      setSocialLinkModal({
        open: true,
        provider: 'facebook',
        inputValue: '',
        error: null,
        saving: false
      });
    }
  };

  // Handle Instagram connect/disconnect
  const handleInstagramConnect = async () => {
    if (socialStatus.instagram.connected) {
      // Show confirmation dialog before disconnecting
      setConfirmUnlink({ open: true, provider: 'instagram', unlinking: false });
    } else {
      // Open modal to enter Instagram username
      setSocialLinkModal({
        open: true,
        provider: 'instagram',
        inputValue: '',
        error: null,
        saving: false
      });
    }
  };

  // iter373 — Normalize + validate social handles to canonical form.
  // Returns { error } on invalid input or { handle } with the canonical
  // value to send to the backend (full profile URL for Facebook,
  // bare username for Instagram).
  const normalizeSocialInput = (provider, rawValue) => {
    const value = (rawValue || '').trim();
    if (!value) return { error: t('accountExtra.fieldRequired') };
    if (/[\s|]/.test(value)) return { error: t('accountExtra.socialNoSpaces') };
    // Unsafe / non-http protocols (javascript:, data:, etc.)
    if (/^[a-z][a-z0-9+.-]*:/i.test(value) && !/^https?:\/\//i.test(value)) {
      return { error: provider === 'facebook' ? t('accountExtra.invalidFacebook') : t('accountExtra.invalidInstagram') };
    }

    if (provider === 'facebook') {
      const FB_RESERVED = ['share', 'sharer', 'sharer.php', 'posts', 'post', 'groups', 'group', 'watch', 'reel', 'reels', 'story.php', 'stories', 'photo', 'photo.php', 'photos', 'events', 'marketplace', 'login', 'pages', 'people', 'hashtag', 'gaming', 'live'];
      const urlMatch = value.match(/^(?:https?:\/\/)?(?:www\.|m\.|web\.|mbasic\.)?(?:facebook\.com|fb\.com)\/(.+)$/i);
      if (urlMatch) {
        const path = urlMatch[1].replace(/\/+$/, '');
        const idMatch = path.match(/^profile\.php\?id=(\d+)$/i);
        if (idMatch) return { handle: `https://www.facebook.com/profile.php?id=${idMatch[1]}` };
        const first = path.split(/[/?#]/)[0].toLowerCase();
        if (path.includes('/') || path.includes('?') || FB_RESERVED.includes(first)) {
          return { error: t('accountExtra.invalidFacebookShare') };
        }
        if (!/^[a-zA-Z0-9._-]{2,60}$/.test(path)) return { error: t('accountExtra.invalidFacebook') };
        return { handle: `https://www.facebook.com/${path}` };
      }
      const uname = value.startsWith('@') ? value.slice(1) : value;
      if (uname.includes('/') || /\.(com|net|org|io|co|me|app|link|ly|info|site|online|store|shop|xyz)$/i.test(uname) || !/^[a-zA-Z0-9._-]{2,60}$/.test(uname) || FB_RESERVED.includes(uname.toLowerCase())) {
        return { error: t('accountExtra.invalidFacebook') };
      }
      return { handle: `https://www.facebook.com/${uname}` };
    }

    // Instagram
    const IG_RESERVED = ['p', 'reel', 'reels', 'stories', 'explore', 'accounts', 'tv', 'direct'];
    const igMatch = value.match(/^(?:https?:\/\/)?(?:www\.)?instagram\.com\/(.+)$/i);
    let uname;
    if (igMatch) {
      const path = igMatch[1].replace(/\/+$/, '');
      const first = path.split(/[/?#]/)[0].toLowerCase();
      if (path.includes('/') || path.includes('?') || IG_RESERVED.includes(first)) {
        return { error: t('accountExtra.invalidInstagramShare') };
      }
      uname = path.startsWith('@') ? path.slice(1) : path;
    } else {
      uname = value.startsWith('@') ? value.slice(1) : value;
      if (uname.includes('/') || /\.(com|net|org|io|co|me|app|link|ly|info|site|online|store|shop|xyz)$/i.test(uname)) return { error: t('accountExtra.invalidInstagram') };
    }
    if (!/^[a-zA-Z0-9._]{1,30}$/.test(uname) || IG_RESERVED.includes(uname.toLowerCase())) {
      return { error: t('accountExtra.invalidInstagram') };
    }
    return { handle: uname };
  };

  // Handle social link modal save
  const handleSocialLinkSave = async () => {
    const { provider, inputValue } = socialLinkModal;
    
    // Normalize + validate input
    const normalized = normalizeSocialInput(provider, inputValue);
    if (normalized.error) {
      setSocialLinkModal(prev => ({ ...prev, error: normalized.error }));
      return;
    }
    
    setSocialLinkModal(prev => ({ ...prev, saving: true, error: null }));
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/${provider}/manual-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ handle: normalized.handle })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update local state with the returned data
        if (provider === 'facebook') {
          setSocialStatus(prev => ({
            ...prev,
            facebook: { connected: true, name: data.name || normalized.handle }
          }));
        } else {
          setSocialStatus(prev => ({
            ...prev,
            instagram: { connected: true, username: data.username || normalized.handle }
          }));
        }
        
        toast.success(
          isRTL 
            ? `تم ربط ${provider === 'facebook' ? 'فيسبوك' : 'انستقرام'} بنجاح`
            : `${provider === 'facebook' ? 'Facebook' : 'Instagram'} linked successfully`
        );
        
        // Close modal
        setSocialLinkModal({ open: false, provider: null, inputValue: '', error: null, saving: false });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to link account');
      }
    } catch (err) {
      setSocialLinkModal(prev => ({
        ...prev,
        saving: false,
        error: err.message || (t('accountExtra.failedToLink'))
      }));
    }
  };

  // Close social link modal
  const closeSocialLinkModal = () => {
    setSocialLinkModal({ open: false, provider: null, inputValue: '', error: null, saving: false });
  };

  // Handle unlink confirmation
  const handleConfirmUnlink = async () => {
    const provider = confirmUnlink.provider;
    if (!provider) return;
    
    setConfirmUnlink(prev => ({ ...prev, unlinking: true }));
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/${provider}/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Update local state
        setSocialStatus(prev => ({
          ...prev,
          [provider]: { connected: false, name: null, username: null }
        }));
        
        toast.success(
          isRTL 
            ? `تم إلغاء ربط ${provider === 'facebook' ? 'فيسبوك' : 'انستقرام'}`
            : `${provider === 'facebook' ? 'Facebook' : 'Instagram'} disconnected`
        );
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (err) {
      toast.error(t('accountExtra.failedToUnlink'));
    } finally {
      setConfirmUnlink({ open: false, provider: null, unlinking: false });
    }
  };

  // Cancel unlink
  const handleCancelUnlink = () => {
    setConfirmUnlink({ open: false, provider: null, unlinking: false });
  };

  // iter347 — "Boost Your Ads" in Growth Tips must NEVER open the
  // Boost package modal directly (that leaks the recharge/insufficient
  // flow before the user has chosen which ad to boost). Route to the
  // dedicated ad-picker page which then opens the modal only after
  // the user selects a specific ad. Empty-state (no eligible ads) is
  // handled by ChooseAdToBoost itself.
  const handleBoostClick = () => {
    navigate('/wallet/boost/choose-ad', { state: { from: '/account' } });
  };
  
  // Handle opening boost modal for a specific ad
  const handleBoostAd = (ad, e) => {
    if (e) e.stopPropagation();
    
    if (!canBoostAd(ad)) {
      toast.info(t('accountExtra.adMustBePublished'));
      return;
    }
    
    setSelectedAdForBoost(ad);
    setBoostModalOpen(true);
  };
  
  // Handle boost success - refresh user data
  const handleBoostSuccess = async () => {
    // Refresh user data to get updated coin balance
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        updateUser(data);
      }
    } catch (e) {
      console.error('Error refreshing after boost:', e);
    }
  };

  // Fetch user's favorites
  const fetchFavorites = async () => {
    if (!token) return;
    
    setLoadingFavorites(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/favorites?limit=50`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (e) {
      console.error('Error fetching favorites:', e);
    } finally {
      setLoadingFavorites(false);
    }
  };
  
  // Fetch favorites when tab changes
  useEffect(() => {
    if (myAdsTab === 'favorites') {
      fetchFavorites();
    }
  }, [myAdsTab, token]);

  // Handle "Share on Facebook" click
  const handleShareOnFacebook = async () => {
    // Public seller profile — recipients can actually open this (my-ads is private)
    const shareUrl = buildShareUrl(`/seller/${user?.user_id || ''}`);
    const shareText = t('accountExtra.shareListingsText');

    // Native share sheet first (Capacitor on Android, Web Share on browsers)
    const res = await shareNative({ title: 'Wassla', text: shareText, url: shareUrl });
    if (res.completed) return;

    // Fallback: Open Facebook sharer
    const fbSharerUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(fbSharerUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
  };

  // Initialize form when user data changes
  const originalFormData = useMemo(() => ({
    name: user?.name || '',
    phone: user?.phone || '',
    whatsapp_phone: user?.whatsapp_phone || '',
    messenger_link: user?.messenger_link || '',
    country_code: user?.country_code || 'SD',
    region: user?.region || user?.state_key || '',
    city: user?.city || ''
  }), [user]);

  // Check for unsaved changes
  useEffect(() => {
    if (isEditing) {
      const hasChanges = JSON.stringify(editForm) !== JSON.stringify(originalFormData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [editForm, originalFormData, isEditing]);
  // Add body class when editing to move floating assistant up
  useEffect(() => {
    if (isEditing) {
      document.body.classList.add('profile-edit-mode');
    } else {
      document.body.classList.remove('profile-edit-mode');
    }
    return () => {
      document.body.classList.remove('profile-edit-mode');
    };
  }, [isEditing]);

  // ────────────────────────────────────────────────────────────────────
  // Nav-guard: while editing the profile, intercept all global navigation
  // (BottomNav tabs, browser back, tab close) so users do not silently
  // discard in-progress edits. Bug fix 2026-05-22.
  // ────────────────────────────────────────────────────────────────────
  const { setGuard } = useNavGuard();
  const pendingNavRef = useRef(null);

  useEffect(() => {
    if (!isEditing) {
      setGuard(null);
      return undefined;
    }
    // Allow staying on /account; block everything else.
    setGuard((targetPath) => {
      if (!targetPath || targetPath === '/account' || targetPath.startsWith('/account?')) {
        return true;
      }
      if (hasUnsavedChanges) {
        pendingNavRef.current = targetPath;
        setUnsavedDialogOpen(true);
        return false;
      }
      // No unsaved changes — silently exit edit mode and let the nav happen.
      setIsEditing(false);
      return true;
    });
    return () => setGuard(null);
  }, [isEditing, hasUnsavedChanges, setGuard]);

  // Block the tab-close / hard-refresh path when the form is dirty.
  useEffect(() => {
    if (!isEditing || !hasUnsavedChanges) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isEditing, hasUnsavedChanges]);

  // Block the browser back button when the form is dirty.
  useEffect(() => {
    if (!isEditing || !hasUnsavedChanges) return undefined;
    // Push a marker state so a single Back press fires popstate without
    // actually leaving /account.
    window.history.pushState({ wasslaProfileEditGuard: true }, '');
    const onPopState = () => {
      // Restore the marker so subsequent Back presses keep firing here.
      window.history.pushState({ wasslaProfileEditGuard: true }, '');
      pendingNavRef.current = '__back__';
      setUnsavedDialogOpen(true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isEditing, hasUnsavedChanges]);


  // Get user role — normalize old DB keys to canonical set
  const rawRole = user?.role || user?.account_type || user?.profile_type || 'general';
  const ROLE_ALIAS = { trader: 'seller', service_provider: 'service', individual: 'general' };
  const userRole = ROLE_ALIAS[rawRole] || rawRole;
  const RoleIcon = ROLE_ICONS[userRole] || UserCircle;
  
  // Chevron based on direction
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  // Handle wallet/email copy
  const handleCopyWallet = () => {
    const textToCopy = user?.wallet_number || user?.email || '';
    navigator.clipboard.writeText(textToCopy).then(() => {
      setWalletCopied(true);
      toast.success(t('accountExtra.copiedMsg'));
      setTimeout(() => setWalletCopied(false), 2000);
    }).catch(() => {
      toast.error(t('accountExtra.copyFailedMsg'));
    });
  };

  // Get country display name
  const getCountryDisplay = (code) => {
    const country = COUNTRIES.find(c => c.code === code);
    if (!country) return code;
    return isRTL ? country.name_ar : country.name_en;
  };

  // Handle edit mode
  const handleStartEdit = () => {
    setEditForm({
      name: user?.name || '',
      phone: user?.phone || '',
      whatsapp_phone: user?.whatsapp_phone || '',
      messenger_link: user?.messenger_link || '',
      country_code: user?.country_code || 'SD',
      region: user?.region || user?.state_key || '',
      city: user?.city || ''
    });
    setIsEditing(true);
    setHasUnsavedChanges(false);
    setMenuOpen(false);
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setUnsavedDialogOpen(true);
    } else {
      setIsEditing(false);
    }
  };

  const confirmDiscardChanges = () => {
    setIsEditing(false);
    setHasUnsavedChanges(false);
    setUnsavedDialogOpen(false);
    const pending = pendingNavRef.current;
    pendingNavRef.current = null;
    if (pending === '__back__') {
      // Pop the marker we pushed in the popstate-guard effect.
      window.history.back();
    } else if (pending) {
      navigate(pending);
    }
  };

  // Handle country change — never silently erase State/City; if a saved
  // location exists, ask the user whether to keep or clear it (iter366).
  const handleCountryChange = (code) => {
    setCountryOpen(false);
    if (code === editForm.country_code) return;
    if ((editForm.region || '').trim() || (editForm.city || '').trim()) {
      setCountryConfirm({ open: true, code });
      return;
    }
    setEditForm(prev => ({ ...prev, country_code: code }));
  };

  const applyCountryChange = (clearLocation) => {
    const code = countryConfirm.code;
    setCountryConfirm({ open: false, code: null });
    if (!code) return;
    setEditForm(prev => ({
      ...prev,
      country_code: code,
      region: clearLocation ? '' : prev.region,
      city: clearLocation ? '' : prev.city,
    }));
  };

  // Safe response parser - reads body ONCE to avoid "body stream already read" error
  const parseResponse = async (res) => {
    try {
      const raw = await res.text(); // Read body ONCE and only once
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = { raw };
      }
      return { raw, data, ok: res.ok, status: res.status };
    } catch (e) {
      console.error('[parseResponse] Error reading response:', e);
      return { raw: '', data: null, ok: res.ok, status: res.status };
    }
  };

  // Handle save profile
  const handleSaveProfile = async () => {
    if (!editForm.name.trim()) {
      toast.error(t('toast.nameRequired'));
      return;
    }

    const normPhone = normalizePhoneForCountry(editForm.phone, editForm.country_code);
    if (!normPhone.ok) {
      toast.error(t('toast.invalidPhone'));
      return;
    }
    const normWhatsapp = normalizePhoneForCountry(editForm.whatsapp_phone, editForm.country_code);
    if (!normWhatsapp.ok) {
      toast.error(t('toast.invalidWhatsapp'));
      return;
    }
    if (!isValidMessenger(editForm.messenger_link)) {
      toast.error(t('toast.invalidMessenger'));
      return;
    }

    setSaving(true);
    
    const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const API_URL = `${BASE_URL}/api/auth/profile`;
    
    // Region is now always a free text field, no state_key validation needed
    // Backend PATCH treats null as "no change" but '' as "clear the field" —
    // send '' for blanks so users can actually clear optional fields (iter366).
    const payload = {
      name: editForm.name.trim(),
      phone: normPhone.value ?? '',
      whatsapp_phone: normWhatsapp.value ?? '',
      messenger_link: editForm.messenger_link?.trim() ?? '',
      country_code: editForm.country_code || null,
      region: editForm.region?.trim() ?? '',
      city: editForm.city?.trim() ?? '',
      state_key: null // Deprecated: region is now free text
    };

    if (!BASE_URL) {
      toast.error('Configuration error: API URL not set');
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(API_URL, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      // Parse response ONCE - never call .text() or .json() again on this response
      const { data, ok, status } = await parseResponse(response);
      
      if (ok && data?.success) {
        if (data.user) {
          // CRITICAL SECURITY CHECK: Verify response user ID matches current user
          if (data.user.user_id !== user.user_id) {
            console.error('[Profile Save] SECURITY VIOLATION: Response user ID mismatch!', {
              expected: user.user_id,
              received: data.user.user_id
            });
            throw new Error('Profile update returned wrong user data. Please try again or contact support.');
          }
          updateUser(data.user);
        }
        setIsEditing(false);
        setHasUnsavedChanges(false);
        toast.success(t('toast.profileUpdated'));
        
        // Try to claim profile completion bonus (separate request)
        try {
          const bonusResponse = await fetch(`${BASE_URL}/api/coins/complete-profile`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const { data: bonusData } = await parseResponse(bonusResponse);
          
          if (bonusData?.granted) {
            updateUser({ coins: bonusData.new_balance });
            toast.success(
              t('acco.youEarned2CoinsFor'),
              { duration: 5000 }
            );
          }
        } catch {
          // Silently ignore bonus errors
        }
      } else {
        // Show specific error from backend with user-friendly messages
        let errorMsg = data?.detail || data?.message || '';
        
        if (status === 404) {
          errorMsg = t('acco.couldNotSaveDataEndpoint');
        } else if (status === 401) {
          errorMsg = t('acco.sessionExpiredPleaseLogIn');
        } else if (status === 403) {
          errorMsg = t('acco.youDoNotHavePermission');
        } else if (status === 422) {
          errorMsg = t('acco.someFieldsAreInvalidPlease');
        } else if (!errorMsg) {
          errorMsg = isRTL 
            ? `فشل الحفظ (${status}). الرجاء المحاولة لاحقاً.`
            : `Save failed (${status}). Please try again later.`;
        }
        
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('[PROFILE SAVE] Exception:', err);
      
      if (err.message?.includes('Failed to fetch')) {
        toast.error(t('acco.connectionErrorCheckYourInternet'));
      } else if (err.message?.includes('body stream already read')) {
        // This shouldn't happen anymore, but just in case
        toast.error(t('accountExtra.saveError'));
      } else {
        toast.error(err.message || t('toast.connectionError'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error(t('accountExtra.allFieldsRequired'));
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error(t('accountExtra.passwordMinLength'));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('accountExtra.passwordsMismatch'));
      return;
    }

    setChangingPassword(true);
    
    try {
      const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const response = await fetch(`${BASE_URL}/api/me/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          old_password: passwordForm.oldPassword,
          new_password: passwordForm.newPassword
        })
      });

      const { data, ok } = await parseResponse(response);
      
      if (ok && data?.success) {
        toast.success(t('accountExtra.passwordChangedOk'));
        setShowChangePassword(false);
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        const errorMsg = data?.detail || data?.message || 'Failed to change password';
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('[CHANGE PASSWORD] Exception:', err);
      toast.error(err.message || (t('accountExtra.passwordChangeFailed')));
    } finally {
      setChangingPassword(false);
    }
  };

  // Handle update email (NEW)
  const handleUpdateEmail = async () => {
    // Validation
    if (!emailForm.currentPassword || !emailForm.newEmail) {
      toast.error(t('accountExtra.allFieldsRequired'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.newEmail)) {
      toast.error(t('accountExtra.invalidEmailMsg'));
      return;
    }

    // Check if new email is same as current
    if (emailForm.newEmail.toLowerCase() === user?.email?.toLowerCase()) {
      toast.error(t('accountExtra.emailSameAsCurrent'));
      return;
    }

    setUpdatingEmail(true);
    
    try {
      const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const response = await fetch(`${BASE_URL}/api/me/update-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: emailForm.currentPassword,
          new_email: emailForm.newEmail
        })
      });

      const { data, ok } = await parseResponse(response);
      
      if (ok && data?.success) {
        toast.success(
          isRTL 
            ? 'تم تحديث البريد الإلكتروني بنجاح' 
            : data?.message || 'Email updated successfully'
        );
        
        // Update user context with new email
        updateUser({ email: emailForm.newEmail });
        
        setShowUpdateEmail(false);
        setEmailForm({ currentPassword: '', newEmail: '' });
        
        // Show additional info if verification is mocked — pick localized variant
        const localizedNote = isRTL
          ? (data?.note_ar || data?.note)
          : (data?.note_en || data?.note);
        if (localizedNote) {
          setTimeout(() => {
            toast.info(localizedNote, { duration: 5000 });
          }, 1000);
        }
      } else {
        const errorMsg = data?.detail || data?.message || 'Failed to update email';
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('[UPDATE EMAIL] Exception:', err);
      toast.error(err.message || (t('accountExtra.emailUpdateFailed')));
    } finally {
      setUpdatingEmail(false);
    }
  };

  // iter368 — Clear Temporary Storage: removes ONLY disposable, safely
  // regeneratable data (browser Cache Storage: cached images/thumbnails/
  // static assets, plus per-session volatile UI state). localStorage is
  // NEVER touched — it holds the auth token, language, theme, favourites,
  // onboarding state and location settings. No server-side data involved.
  const handleClearCache = async () => {
    if (clearingCache) return;
    setClearingCache(true);
    try {
      if (typeof caches !== 'undefined' && caches?.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      try { sessionStorage.clear(); } catch { /* non-fatal */ }
      setShowClearCacheDialog(false);
      toast.success(t('account.tempStorageCleared'));
    } catch (err) {
      console.error('[CLEAR CACHE] Error:', err);
      toast.error(t('accountExtra.clearCacheFailed'));
    } finally {
      setClearingCache(false);
    }
  };

  // Handle photo upload
  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  // Downscale large images client-side before upload (max 1280px, JPEG 0.85)
  const compressImage = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX = 1280;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        if (scale >= 1 && file.size < 800 * 1024) { resolve(file); return; }
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file),
          'image/jpeg',
          0.85
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    } catch { resolve(file); }
  });

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('toast.selectImage'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('toast.imageTooLarge'));
      return;
    }

    // Show the chosen photo immediately while it uploads
    const previewUrl = URL.createObjectURL(file);
    setPhotoPreview(previewUrl);
    setUploadingPhoto(true);
    try {
      const upload = await compressImage(file);
      const formData = new FormData();
      formData.append('files', upload);

      // iter282 — use getApiBase() so live wassla.tech hits same-origin
      // /api/upload (the build-time REACT_APP_BACKEND_URL points at
      // preview and is unreachable cross-origin from the live domain).
      const response = await fetch(`${getApiBase()}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) throw new Error('upload_failed');
      const data = await response.json();
      const imageUrl = data.images?.[0];
      if (!imageUrl) throw new Error('upload_failed');

      const updateResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ avatar: imageUrl })
      });
      if (!updateResponse.ok) throw new Error('upload_failed');
      const updateData = await updateResponse.json();
      updateUser({ ...updateData.user });
      toast.success(t('toast.photoUpdated'));
    } catch (err) {
      // Upload failed — the existing profile image is kept untouched
      toast.error(t('toast.photoUploadError'));
    } finally {
      setUploadingPhoto(false);
      setPhotoPreview(null);
      URL.revokeObjectURL(previewUrl);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/welcome');
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      // iter359 — real backend flow: POST /api/account/request-deletion
      // (password-confirmed). The old call targeted
      // a non-existent auth deletion route and always failed.
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account/request-deletion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: deletePassword }),
      });

      if (response.ok) {
        toast.success(t('toast.accountDeleted'));
        setDeleteDialogOpen(false);
        logout();
        navigate('/welcome');
      } else {
        let detail = '';
        try {
          const err = await response.json();
          detail = typeof err?.detail === 'string' ? err.detail : '';
        } catch (e) { /* ignore parse errors */ }
        if (detail === 'Incorrect password') {
          toast.error(t('account.deleteWrongPassword'));
        } else {
          toast.error(detail || t('toast.deleteAccountFailed'));
        }
      }
    } catch (err) {
      toast.error(t('toast.connectionError'));
    } finally {
      setDeleting(false);
      setDeletePassword('');
    }
  };

  // Recall confirmation state
  const [recallConfirm, setRecallConfirm] = useState({ open: false, adId: null, adTitle: '' });
  const [recallLoading, setRecallLoading] = useState(false);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, adId: null, adTitle: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Request recall (opens confirmation)
  const requestRecallAd = (adId, adTitle, e) => {
    e.stopPropagation();
    setRecallConfirm({ open: true, adId, adTitle });
  };

  // Execute recall after confirmation
  const executeRecallAd = async () => {
    const adId = recallConfirm.adId;
    setRecallLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${adId}/recall`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(t('accountExtra.adRecalledOk'));
        fetchDashboardData(); // Refresh ads list
      } else {
        const data = await response.json();
        toast.error(data.detail || (t('accountExtra.recallFailed')));
      }
    } catch (err) {
      toast.error(t('accountExtra.connectionError'));
    } finally {
      setRecallLoading(false);
      setRecallConfirm({ open: false, adId: null, adTitle: '' });
    }
  };

  // Cancel recall
  const cancelRecallAd = () => {
    setRecallConfirm({ open: false, adId: null, adTitle: '' });
  };

  // Request delete (opens confirmation)
  const requestDeleteAd = (adId, adTitle, e) => {
    e.stopPropagation();
    setDeleteConfirm({ open: true, adId, adTitle });
  };

  // Execute delete after confirmation
  const executeDeleteAd = async () => {
    const adId = deleteConfirm.adId;
    setDeleteLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${adId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(t('accountExtra.adDeletedOk'));
        fetchDashboardData(); // Refresh ads list
      } else {
        const data = await response.json();
        toast.error(data.detail || (t('accountExtra.deleteFailed')));
      }
    } catch (err) {
      toast.error(t('accountExtra.connectionError'));
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm({ open: false, adId: null, adTitle: '' });
    }
  };

  // Cancel delete
  const cancelDeleteAd = () => {
    setDeleteConfirm({ open: false, adId: null, adTitle: '' });
  };

  // ── Ad lifecycle actions (canonical state transitions) ──
  const handleHideAd = async (adId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${adId}/hide`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Ad hidden');
        fetchDashboardData();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to hide ad');
      }
    } catch { toast.error('Connection error'); }
  };

  const handleRestoreAd = async (adId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${adId}/restore`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Ad restored');
        fetchDashboardData();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to restore ad');
      }
    } catch { toast.error('Connection error'); }
  };

  const handleMarkSold = async (adId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${adId}/mark-sold`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Marked as sold');
        trackEvent('ad_mark_sold', { ad_id: adId }, user?.user_id);
        fetchDashboardData();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to mark sold');
      }
    } catch { toast.error('Connection error'); }
  };

  const handleRepublishAd = async (adId, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${adId}/republish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Submitted for review');
        trackEvent('ad_publish', { ad_id: adId }, user?.user_id);
        fetchDashboardData();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to republish');
      }
    } catch { toast.error('Connection error'); }
  };

  // Helper functions for ad actions — canonical 6-state system
  const canDeleteAd = (status) => {
    const canonical = normalizeStatus(status);
    return ['draft', 'rejected', 'hidden_by_user', 'sold'].includes(canonical);
  };
  const canHideAd = (status) => normalizeStatus(status) === 'active';
  const canRestoreAd = (status) => normalizeStatus(status) === 'hidden_by_user';
  const canMarkSold = (status) => normalizeStatus(status) === 'active';
  const canRepublish = (status) => {
    const canonical = normalizeStatus(status);
    return ['sold', 'draft', 'rejected', 'hidden_by_user'].includes(canonical);
  };

  // Get avatar URL — delegate to the shared resolver so legacy
  // /api/uploads paths are rewritten correctly on web AND inside the
  // Android app (same fix as the Seller Profile page).
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    return toAbsoluteUrl(avatarPath);
  };

  // Get ad status info — canonical 6-state system
  const getAdStatus = (ad) => {
    const canonical = normalizeStatus(ad.status);
    const badge = getStatusBadge(canonical);
    
    const colorMap = {
      active: 'green',
      draft: 'gray',
      pending_review: 'blue',
      hidden_by_user: 'yellow',
      sold: 'red',
      rejected: 'red',
    };
    
    return {
      key: canonical,
      label: t(badge.labelKey),
      color: colorMap[canonical] || 'gray',
      badgeClass: badge.color,
    };
  };

  const filteredCountries = useMemo(() => COUNTRIES, []);

  // Stable onChange handler - memoized to prevent unnecessary re-renders
  const handleFieldChange = useCallback((fieldName) => (e) => {
    const value = e.target.value;
    setEditForm(prev => ({ ...prev, [fieldName]: value }));
  }, []);

  // ────────────────────────────────────────────────────────────────────
  // CANONICAL SETTINGS MENU CONFIG
  // One ordered array used by every user. Items are only ever HIDDEN
  // (`visible: false`) by truly user-specific gates — never re-ordered.
  // Spec-locked order (do not reshuffle):
  //   1 Edit Profile · 2 Change Password · 3 Update Email · 4 Language ·
  //   5 Dark Mode · 6 Change Profile Type · 7 Reset Onboarding ·
  //   8 Low Bandwidth Mode · 9 Share Anonymous Usage Data ·
  //   10 Clear Temporary Storage · 11 Notification Preferences
  // ────────────────────────────────────────────────────────────────────
  const isEmailUser = user?.provider === 'email';
  const SETTINGS_MENU_CONFIG = [
    {
      id: 'edit-profile',
      kind: 'action',
      icon: <Settings size={20} />,
      label: t('accountExtra.editProfileMenu'),
      onClick: handleStartEdit,
    },
    {
      id: 'change-password',
      kind: 'action',
      icon: <ShieldCheck size={20} />,
      label: t('accountExtra.changePasswordMenu'),
      visible: isEmailUser,
      onClick: () => { setMenuOpen(false); setShowChangePassword(true); },
    },
    {
      id: 'update-email',
      kind: 'action',
      icon: <Mail size={20} />,
      label: t('accountExtra.updateEmailMenu'),
      visible: isEmailUser,
      onClick: () => { setMenuOpen(false); setShowUpdateEmail(true); },
    },
    {
      id: 'language',
      kind: 'control',
      icon: <Languages size={20} />,
      label: t('accountExtra.languageMenu'),
      control: (
        <button className="settings-pill-btn" onClick={toggleLanguage} data-testid="lang-toggle-btn">
          {t('acco.text')}
        </button>
      ),
    },
    {
      id: 'dark-mode',
      kind: 'control',
      icon: <Moon size={20} />,
      label: t('account.darkMode'),
      control: <Switch checked={isDarkMode} onCheckedChange={toggleTheme} />,
    },
    {
      id: 'change-profile-type',
      kind: 'action',
      testid: 'profile-type-btn',
      icon: <Users size={20} />,
      label: t('account.changeProfileType'),
      onClick: () => { setMenuOpen(false); setShowProfileTypePicker(true); },
    },
    {
      id: 'reset-onboarding',
      kind: 'action',
      testid: 'reset-onboarding-btn',
      icon: <RefreshCw size={20} />,
      label: t('account.resetOnboarding'),
      onClick: () => {
        localStorage.removeItem('wassla_profile_type');
        localStorage.removeItem('wassla_profile_type_set_at');
        localStorage.removeItem('hasCompletedProfileSelection');
        localStorage.removeItem('hasSeenLadiesHubIntro');
        localStorage.removeItem('ladies_hub_verified_v1');
        localStorage.removeItem('wassla_hub_welcomed_v1');
        localStorage.removeItem('wassla_hub_auth_dismissed_v1');
        localStorage.removeItem('studentHub.levelModalDismissed');
        sessionStorage.removeItem('wassla_profile_picker_dismissed_session');
        toast.success(t('account.onboardingReset'), { duration: 3000 });
      },
    },
    {
      id: 'low-bandwidth',
      kind: 'control',
      icon: <Wifi size={20} />,
      label: t('account.lowBandwidthMode'),
      control: (
        <Switch
          checked={isLowBandwidth}
          onCheckedChange={(checked) => {
            const success = toggleLowBandwidth(checked);
            if (success) {
              const message = checked
                ? t('accountExtra.lowBandwidthEnabled')
                : t('accountExtra.lowBandwidthDisabled');
              toast.success(message, { duration: 3000 });
            } else {
              toast.error(t('accountExtra.updateSettingFailed'));
            }
          }}
        />
      ),
    },
    {
      id: 'analytics',
      kind: 'control',
      testid: 'analytics-toggle',
      icon: <PieChart size={20} />,
      label: t('accountExtra.shareAnonymousData'),
      control: (
        <Switch
          checked={!analyticsOptOut}
          onCheckedChange={async (checked) => {
            const optOut = !checked;
            const success = await updateAnalyticsPreference(optOut);
            if (success) {
              setAnalyticsOptOut(optOut);
              const message = optOut
                ? t('accountExtra.analyticsDisabled')
                : t('accountExtra.analyticsEnabled');
              toast.success(message, { duration: 3000 });
            } else {
              toast.error(t('accountExtra.updateSettingFailed'));
            }
          }}
        />
      ),
    },
    {
      id: 'clear-cache',
      kind: 'action',
      icon: <Trash2 size={20} />,
      label: t('account.clearAppCache'),
      onClick: () => { setMenuOpen(false); setShowClearCacheDialog(true); },
    },
    {
      id: 'notifications',
      kind: 'action',
      icon: <Bell size={20} />,
      label: t('account.notificationSettings'),
      onClick: () => { setMenuOpen(false); setShowNotificationSettings(true); },
    },
  ];

  return (
    <div 
      className={`account-page ${isEditing ? 'editing-mode' : ''} overflow-x-hidden`} 
      dir={isRTL ? 'rtl' : 'ltr'}
      lang={isRTL ? 'ar' : 'en'}
      data-testid="account-page"
    >
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoSelect}
        accept="image/*"
        style={{ display: 'none' }}
      />
      
      {/* PREMIUM TOP HEADER - Curved wave design */}
      <AccountTopHeader
        coverColor="#1F6F3A"
        name={user?.name || t('account.guest')}
        email={user?.email}
        walletNumber={user?.wallet_number}
        avatarUrl={photoPreview || (user?.avatar ? getAvatarUrl(user.avatar) : null)}
        avatarFallback={user?.name?.charAt(0)?.toUpperCase() || null}
        createdAt={user?.created_at}
        onEditPhoto={handlePhotoClick}
        onMenuClick={() => setMenuOpen(true)}
        onCopy={handleCopyWallet}
        copied={walletCopied}
        isRTL={isRTL}
        isLoading={uploadingPhoto}
      />
      
      {/* iter369 — role badge ("Browse & Shop" etc.) removed: redundant with
          Shop in the bottom nav; profile type stays changeable from Settings. */}
      {/* Quick Action Row — HIDDEN while editing so the form is the only focus
          (the cards were navigating away & dropping in-progress edits — bug fixed 2026-05-22) */}
      {!isEditing && (
        <div className="quick-actions-row" data-testid="quick-actions-row">
          <button
            className="qa-btn"
            onClick={() => navigate('/my-ads')}
            data-testid="qa-my-ads"
          >
            <Package size={16} />
            <span>{t('account.myAds')}</span>
          </button>
          <button
            className="qa-btn"
            onClick={() => navigate('/favorites')}
            data-testid="qa-saved"
          >
            <Heart size={16} />
            <span>{t('account.favorites')}</span>
          </button>
          <button
            className="qa-btn"
            onClick={() => navigate('/account/business')}
            data-testid="qa-my-business"
          >
            <Briefcase size={16} />
            <span>{isRTL ? 'دفتر الأعمال' : 'My Business'}</span>
          </button>
          {!COINS_DISABLED && (
          <button
            className="qa-btn"
            onClick={() => navigate('/wallet')}
            data-testid="qa-wallet"
          >
            <Wallet size={16} />
            <span>{t('account.wallet')}</span>
          </button>
          )}
        </div>
      )}

      {/* iter369 — compact Safety Tips (3 tips + "View all safety tips"
          modal) so account info isn't pushed down. Same yellow styling. */}
      {!isEditing && (
        <div style={{ padding: '0 14px' }} data-testid="account-safety-tips">
          <SafetyTipsCard variant="compact" />
        </div>
      )}

      {/* EDIT FORM */}
      {isEditing ? (
        <section className="edit-form-section overflow-x-hidden px-4">
          <div className="form-card max-w-full overflow-hidden">
            <h3 className={`form-title ${isRTL ? 'text-right' : 'text-left'}`}>{t('account.editInfo')}</h3>
            
            <FormRow label={t('account.name')} isRTL={isRTL}>
              <EditInput
                name="name"
                value={editForm.name}
                onChange={handleFieldChange('name')}
                placeholder={t('account.namePlaceholder')}
                isRTL={isRTL}
                testId="name-input"
              />
            </FormRow>

            <FormRow label={t('account.phone')} isRTL={isRTL}>
              <EditInput
                name="phone"
                value={editForm.phone}
                onChange={handleFieldChange('phone')}
                placeholder="+249 xxx xxx xxxx"
                type="tel"
                isRTL={isRTL}
                testId="phone-input"
              />
            </FormRow>

            <FormRow 
              label={t('account.whatsappPhone')}
              hint={t('accountExtra.whatsappHint')}
              isRTL={isRTL}
            >
              <EditInput
                name="whatsapp_phone"
                value={editForm.whatsapp_phone}
                onChange={handleFieldChange('whatsapp_phone')}
                placeholder="+249 xxx xxx xxxx"
                type="tel"
                isRTL={isRTL}
                testId="whatsapp-phone-input"
              />
            </FormRow>

            <FormRow 
              label={t('account.messengerLink')}
              hint={t('accountExtra.messengerHint')}
              isRTL={isRTL}
            >
              <EditInput
                name="messenger_link"
                value={editForm.messenger_link}
                onChange={handleFieldChange('messenger_link')}
                placeholder={isRTL ? 'https://m.me/اسم_المستخدم' : 'https://m.me/username'}
                type="url"
                isRTL={isRTL}
                testId="messenger-link-input"
              />
            </FormRow>

            <FormRow label={t('account.country')} isRTL={isRTL}>
              <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                <PopoverTrigger asChild>
                  <button data-testid="country-picker-trigger" className={`w-full h-12 px-4 rounded-xl border border-neutral-200 dark:border-neutral-600 dark:bg-slate-800 flex items-center gap-2 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                    <Globe size={16} className="text-neutral-500 shrink-0" />
                    <span className="flex-1 min-w-0 truncate">{getCountryDisplay(editForm.country_code)}</span>
                    <ChevronsUpDown size={16} className="text-neutral-400 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="country-popover w-[280px]" align={isRTL ? 'end' : 'start'}>
                  <Command>
                    <CommandInput placeholder={t('account.searchCountry')} />
                    <CommandList>
                      <CommandEmpty>{t('account.noCountryFound')}</CommandEmpty>
                      <CommandGroup>
                        {filteredCountries.map((country) => (
                          <CommandItem
                            key={country.code}
                            value={`${country.name_en} ${country.name_ar}`}
                            onSelect={() => handleCountryChange(country.code)}
                          >
                            <Check className={`check-icon ${editForm.country_code === country.code ? 'visible' : ''}`} />
                            {isRTL ? country.name_ar : country.name_en}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </FormRow>

            <FormRow 
              label={t('account.stateRegion')}
              hint={t('accountExtra.regionHint')}
              isRTL={isRTL}
            >
              <EditInput
                name="region"
                value={editForm.region}
                onChange={handleFieldChange('region')}
                placeholder={t('accountExtra.regionPlaceholder')}
                isRTL={isRTL}
                testId="state-region-input"
              />
            </FormRow>

            <FormRow label={t('account.city')} isRTL={isRTL}>
              <EditInput
                name="city"
                value={editForm.city}
                onChange={handleFieldChange('city')}
                placeholder={t('accountExtra.cityOptional')}
                isRTL={isRTL}
                testId="city-input"
              />
            </FormRow>
          </div>

          {/* STICKY SAVE/CANCEL FOOTER - Fixed for RTL */}
          <div className="edit-actions-footer flex gap-3 w-full px-4 pb-6">
            <button 
              className="profile-save-btn flex-1 h-12 rounded-2xl" 
              onClick={handleSaveProfile} 
              disabled={saving || !hasUnsavedChanges} 
              data-testid="save-profile-btn"
            >
              {saving ? (
                <><Loader2 size={18} className="animate-spin" /><span>{t('account.saving')}</span></>
              ) : (
                <><Check size={18} /><span>{t('account.save')}</span></>
              )}
            </button>
            <button 
              className="profile-cancel-btn flex-1 h-12 rounded-2xl" 
              onClick={handleCancelEdit} 
              disabled={saving} 
              data-testid="cancel-edit-btn"
            >
              {t('account.cancel')}
            </button>
          </div>
        </section>
      ) : (
        /* DASHBOARD */
        <section className="dashboard-section">
          
          {loadError && !loading && (
            <InlineError onRetry={fetchDashboardData} />
          )}

          {/* A) QUICK SUMMARY - 2x2 Grid */}
          <div className="section-header">
            <h3 className="section-title">
              <BarChart3 size={20} />
              <span>{t('account.overview')}</span>
            </h3>
          </div>

          <div className="stats-grid">
            <div
              className="stat-card clickable"
              onClick={() => navigate('/my-ads?tab=active')}
              data-testid="stat-active-ads"
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-icon-wrapper blue">
                <Package size={22} />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? '—' : stats.activeAds}</span>
                <span className="stat-label">{t('account.activeAds')}</span>
              </div>
              {stats.activeAds === 0 && !loading && (
                <span className="stat-empty">{t('account.noneYet')}</span>
              )}
              <ChevronIcon size={18} className="stat-chevron" />
            </div>

            <div 
              className="stat-card clickable" 
              onClick={() => navigate('/account/views')} 
              data-testid="stat-views"
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-icon-wrapper purple">
                <Eye size={22} />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? '—' : stats.totalViews}</span>
                <span className="stat-label">{t('account.totalViews')}</span>
              </div>
              {stats.totalViews === 0 && !loading && (
                <span className="stat-empty">{t('account.noViewsYet')}</span>
              )}
              <ChevronIcon size={18} className="stat-chevron" />
            </div>

            <div
              className="stat-card clickable"
              onClick={() => navigate('/favorites')}
              data-testid="stat-saved"
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-icon-wrapper gold">
                <Heart size={22} />
              </div>
              <div className="stat-info">
                <span className="stat-number">{favAds?.length ?? 0}</span>
                <span className="stat-label">{t('account.favorites')}</span>
              </div>
              {(favAds?.length ?? 0) === 0 && (
                <span className="stat-empty">{t('account.noneYet')}</span>
              )}
              <ChevronIcon size={18} className="stat-chevron" />
            </div>

            <div 
              className="stat-card clickable" 
              onClick={() => navigate('/messages', { state: { from: '/account' } })} 
              data-testid="stat-responses"
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-icon-wrapper green">
                <Phone size={22} />
              </div>
              <div className="stat-info">
                <span className="stat-number">{loading ? '—' : messageThreadCount}</span>
                <span className="stat-label">{t('account.responses')}</span>
              </div>
              {messageThreadCount === 0 && !loading && (
                <span className="stat-empty">{t('account.noResponsesYet')}</span>
              )}
              <ChevronIcon size={18} className="stat-chevron" />
            </div>
          </div>

          {/* B) PERFORMANCE INSIGHTS */}
          <div className="insights-card">
            <h4 className="card-header">
              <TrendingUp size={18} />
              <span>{t('account.performance')}</span>
            </h4>
            
            {topAd ? (
              <div className="top-ad-row">
                <div className="top-ad-info">
                  <span className="top-ad-label">{t('account.topPerformingAd')}</span>
                  <span className="top-ad-title">{topAd.title}</span>
                </div>
                <div className="top-ad-stats">
                  <span className="top-ad-stat"><Eye size={14} /> {topAd.viewCount || topAd.views || 0}</span>
                </div>
              </div>
            ) : (
              <div className="insights-empty">
                <Sparkles size={24} />
                <p>{t('account.postAdToSeeInsights')}</p>
              </div>
            )}

            <div className="activity-summary">
              <div className="activity-item">
                <span className="activity-value" data-testid="views-today-value">
                  {loading ? '…' : analyticsReady ? (stats.todayViews ?? 0) : t('had.noDataYet')}
                </span>
                <span className="activity-label">{t('account.viewsToday')}</span>
              </div>
              <div className="activity-divider" />
              <div className="activity-item">
                <span className="activity-value" data-testid="views-week-value">
                  {loading ? '…' : analyticsReady ? (stats.weekViews ?? 0) : t('had.noDataYet')}
                </span>
                <span className="activity-label">{t('account.thisWeek')}</span>
              </div>
            </div>
          </div>

          {/* D) WALLET & MONETIZATION */}
          {/* iter347 — Compact wallet card. Removed the paid-flow
              "Top Up" wording (no third-party payment path exists in
              this app anymore); both actions are now earn-focused. */}
          {!COINS_DISABLED && (
          <div className="wallet-card wallet-card--compact" data-testid="account-wallet-card">
            <div className="wallet-header">
              <h4 className="card-header">
                <Wallet size={16} />
                <span>{t('account.wallet')}</span>
              </h4>
            </div>

            <div className="wallet-balance">
              <span className="balance-label" data-testid="account-wallet-balance-label">{t('account.currentBalance')}</span>
              <span className="balance-amount" data-testid="account-wallet-balance-amount">
                {user?.coins || 0} <small>{t('coins')}</small>
              </span>
            </div>

            <div className="wallet-actions">
              <button
                className="wallet-btn primary"
                onClick={() => navigate('/wallet?scroll=earn', { state: { from: '/account' } })}
                data-testid="account-wallet-earn-coins-btn"
              >
                <Sparkles size={16} />
                <span>{isArabic ? 'اكسب عملات' : 'Earn coins'}</span>
              </button>
              <button
                className="wallet-btn secondary"
                onClick={() => navigate('/wallet?scroll=rules', { state: { from: '/account' } })}
                data-testid="account-wallet-how-to-earn-btn"
              >
                <Info size={16} />
                <span>{isArabic ? 'كيفية كسب العملات' : 'How to earn coins'}</span>
              </button>
            </div>
          </div>
          )}

          {/* E) GROWTH & SUGGESTIONS - keeping English/Arabic inline for now as these are optional tips */}
          <div className="suggestions-section">
            <h4 className="card-header">
              <Sparkles size={18} />
              <span>{t('accountExtra.growthTips')}</span>
            </h4>

            <div className="suggestion-cards">
              {stats.activeAds === 0 && (
                <div className="suggestion-card">
                  <div className="suggestion-icon blue"><Plus size={20} /></div>
                  <div className="suggestion-content">
                    <span className="suggestion-title">{t('accountExtra.getStarted')}</span>
                    <span className="suggestion-text">{t('accountExtra.createFirstAdTip')}</span>
                  </div>
                  <button onClick={() => triggerPostAd('/add-ad')}><ChevronIcon size={18} /></button>
                </div>
              )}

              {!COINS_DISABLED && stats.activeAds > 0 && stats.totalViews < 10 && (
                <button 
                  className="suggestion-card suggestion-card-clickable"
                  onClick={handleBoostClick}
                  type="button"
                >
                  <div className="suggestion-icon gold"><Zap size={20} /></div>
                  <div className="suggestion-content">
                    <span className="suggestion-title">{t('accountExtra.boostYourAds')}</span>
                    <span className="suggestion-text">{t('accountExtra.boostDesc')}</span>
                  </div>
                  <ChevronIcon size={18} className="suggestion-chevron" />
                </button>
              )}

              <button 
                className="suggestion-card suggestion-card-clickable"
                onClick={handleShareOnFacebook}
                type="button"
              >
                <div className="suggestion-icon purple"><Facebook size={20} /></div>
                <div className="suggestion-content">
                  <span className="suggestion-title">{t('accountExtra.shareOnFacebook')}</span>
                  <span className="suggestion-text">{t('accountExtra.shareOnFacebookDesc')}</span>
                </div>
                <ChevronIcon size={18} className="suggestion-chevron" />
              </button>
            </div>
          </div>

          {/* F) LINKED ACCOUNTS */}
          <div className="linked-accounts-card">
            <h4 className="card-header">
              <Link2 size={18} />
              <span>{t('account.linkedAccounts')}</span>
            </h4>

            {/* Facebook */}
            <div className="linked-item">
              <div className={`linked-icon facebook ${socialStatus.facebook.connected ? 'connected' : ''}`}>
                <Facebook size={20} />
              </div>
              <div className="linked-info">
                <span className="linked-name">Facebook</span>
                <span className={`linked-status ${socialStatus.facebook.connected ? 'connected' : 'not-connected'}`}>
                  {socialStatus.facebook.connected 
                    ? (t('accountExtra.connected'))
                    : (t('accountExtra.notConnected'))
                  }
                </span>
              </div>
              <button 
                className={`link-btn ${socialStatus.facebook.connected ? 'disconnect' : ''}`}
                onClick={handleFacebookConnect}
                disabled={connectingFacebook}
              >
                {connectingFacebook 
                  ? <Loader2 size={14} className="animate-spin" />
                  : socialStatus.facebook.connected 
                    ? (t('accountExtra.unlinkBtn'))
                    : t('account.link')
                }
              </button>
            </div>

            {/* Instagram */}
            <div className="linked-item">
              <div className={`linked-icon instagram ${socialStatus.instagram.connected ? 'connected' : ''}`}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </div>
              <div className="linked-info">
                <span className="linked-name">Instagram</span>
                <span className={`linked-status ${socialStatus.instagram.connected ? 'connected' : 'not-connected'}`}>
                  {socialStatus.instagram.connected 
                    ? (t('accountExtra.connected'))
                    : (t('accountExtra.notConnected'))
                  }
                </span>
              </div>
              <button 
                className={`link-btn ${socialStatus.instagram.connected ? 'disconnect' : ''}`}
                onClick={handleInstagramConnect}
                disabled={connectingInstagram}
              >
                {connectingInstagram 
                  ? <Loader2 size={14} className="animate-spin" />
                  : socialStatus.instagram.connected 
                    ? (t('accountExtra.unlinkBtn'))
                    : t('account.link')
                }
              </button>
            </div>
          </div>

        </section>
      )}

      {/* SETTINGS MENU */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side={isRTL ? 'right' : 'left'} className="settings-sheet p-0 flex flex-col" data-testid="settings-drawer">
          {/* Fixed Header */}
          <SheetHeader className="settings-sheet-header px-4 py-4 border-b flex-shrink-0">
            <SheetTitle className="settings-sheet-title" data-testid="settings-sheet-title">
              {t('accountExtra.settingsTitle')}
            </SheetTitle>
          </SheetHeader>
          
          {/* Scrollable Content Area */}
          <div 
            className="settings-menu flex-1 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* ────────────────────────────────────────────────────────
                CANONICAL SETTINGS MENU
                One source of truth for ALL users. Order is fixed at the
                config level — items can be HIDDEN by `visible: false` but
                their relative order is never reshuffled.
                Bug fix 2026-05-23: previously the menu was a long inline
                JSX block; users with provider!='email' visually saw items
                appear to shift up, which read as "different order per user".
                ──────────────────────────────────────────────────────── */}
            {SETTINGS_MENU_CONFIG.filter((it) => it.visible !== false).map((it) => {
              if (it.kind === 'control') {
                return (
                  <div
                    key={it.id}
                    className="menu-item-toggle settings-row"
                    data-testid={it.testid || `settings-row-${it.id}`}
                  >
                    <div className="menu-item-left">
                      {it.icon}
                      <span>{it.label}</span>
                    </div>
                    {it.control}
                  </div>
                );
              }
              return (
                <button
                  key={it.id}
                  className="menu-item settings-row"
                  onClick={it.onClick}
                  data-testid={it.testid || `settings-row-${it.id}`}
                >
                  {it.icon}
                  <span>{it.label}</span>
                  <ChevronIcon size={18} />
                </button>
              );
            })}

            {/* About Wassla */}
            <button 
              className="menu-item" 
              onClick={() => { setMenuOpen(false); setShowAboutModal(true); }}
              data-testid="about-wassla-menu-item"
            >
              <Info size={20} />
              <div className="menu-item-text-group">
                <span>{t('accountExtra.aboutWassla')}</span>
                <span className="menu-item-subtitle">
                  {isRTL ? BRAND.slogan.ar : BRAND.slogan.en}
                </span>
              </div>
              <ChevronIcon size={18} />
            </button>

            {/* Admin Moderation Link - only for admins */}
            {user?.is_admin && (
              <button className="menu-item admin-item" onClick={() => { setMenuOpen(false); navigate('/admin/moderation'); }}>
                <Shield size={20} />
                <span>{t('accountExtra.moderationDashboard')}</span>
                <ChevronIcon size={18} />
              </button>
            )}

            {/* Admin Help Analytics Link - only for admins */}
            {user?.is_admin && (
              <button className="menu-item admin-item" onClick={() => { setMenuOpen(false); navigate('/admin/help-analytics'); }} data-testid="help-analytics-link">
                <PieChart size={20} />
                <span>{t('accountExtra.helpAnalyticsMenu')}</span>
                <ChevronIcon size={18} />
              </button>
            )}

            <div className="menu-divider" />

            <button className="menu-item" onClick={() => { setMenuOpen(false); navigate('/privacy-policy'); }}>
              <ShieldCheck size={20} />
              <span>{t('account.privacyPolicy')}</span>
              <ChevronIcon size={18} />
            </button>

            <button className="menu-item" onClick={() => { setMenuOpen(false); navigate('/terms'); }}>
              <FileText size={20} />
              <span>{t('account.termsConditions')}</span>
              <ChevronIcon size={18} />
            </button>

            <button className="menu-item" onClick={() => { setMenuOpen(false); navigate('/contact'); }}>
              <Mail size={20} />
              <span>{t('account.helpSupport')}</span>
              <ChevronIcon size={18} />
            </button>

            <div className="menu-divider" />

            <button className="menu-item logout-item" data-testid="account-logout" onClick={() => { setMenuOpen(false); setLogoutDialogOpen(true); }}>
              <LogOut size={20} />
              <span>{t('account.logout')}</span>
            </button>

            <button className="menu-item delete-item" onClick={() => { setMenuOpen(false); setDeleteDialogOpen(true); }}>
              <Trash2 size={20} />
              <span>{t('account.deleteAccount')}</span>
            </button>

            {/* App About Footer - AliExpress Style */}
            <div 
              style={{
                marginTop: 16,
                padding: '18px 12px 96px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
              }}
              data-testid="app-about-footer"
              className="app-about-footer"
            >
              {/* Logo */}
              <WasslaLogo size="footer" />
              
              {/* Version — read live from the installed package metadata */}
              <AboutVersionLabel isRTL={isRTL} />
              
              {/* Copyright */}
              <div className="app-about-footer-copy">
                {isRTL 
                  ? `© ${new Date().getFullYear()} وصلة. جميع الحقوق محفوظة.`
                  : `© ${new Date().getFullYear()} Wassla. All rights reserved.`}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* DIALOGS */}
      <AlertDialog
        open={unsavedDialogOpen}
        onOpenChange={(o) => {
          setUnsavedDialogOpen(o);
          if (!o) pendingNavRef.current = null;
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('accountExtra.unsavedChangesTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('accountExtra.unsavedChangesDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel className="btn btnSecondaryOrange">{t('accountExtra.keepEditing')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardChanges} className="btn btnPrimaryOrange">
              {t('accountExtra.discardBtn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Country change — keep or clear existing State/City (iter366) */}
      <AlertDialog
        open={countryConfirm.open}
        onOpenChange={(o) => { if (!o) setCountryConfirm({ open: false, code: null }); }}
      >
        <AlertDialogContent data-testid="country-change-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('accountExtra.countryChangeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('accountExtra.countryChangeDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel
              className="btn btnSecondaryOrange"
              onClick={() => applyCountryChange(false)}
              data-testid="country-keep-location-btn"
            >
              {t('accountExtra.keepLocationBtn')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="btn btnPrimaryOrange"
              onClick={() => applyCountryChange(true)}
              data-testid="country-clear-location-btn"
            >
              {t('accountExtra.clearLocationBtn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('account.logout')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('accountExtra.logoutConfirmMsg')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel className="btn btnSecondaryOrange">{t('account.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout} className="btn btnPrimaryOrange">
              {t('account.logout')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeletePassword(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="delete-title">{t('account.deleteAccountTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('account.deleteAccountMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            type="password"
            className="delete-account-password"
            placeholder={t('account.deleteAccountPasswordPlaceholder')}
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            autoComplete="current-password"
            data-testid="delete-account-password-input"
          />
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel className="btn btnSecondaryOrange">{t('account.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleting || !deletePassword.trim()}
              className="btn delete-action"
              data-testid="delete-account-confirm-btn"
            >
              {deleting ? (t('accountExtra.deletingAccount')) : t('account.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="change-password-modal">
          <DialogHeader>
            <DialogTitle>{t('accountExtra.changePasswordMenu')}</DialogTitle>
          </DialogHeader>
          <div className="password-form-content" style={{ marginTop: '1.5rem' }}>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                {t('accountExtra.currentPasswordLabel')}
              </label>
              <Input
                type="password"
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                placeholder={t('accountExtra.enterCurrentPassword')}
                disabled={changingPassword}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                {t('accountExtra.newPasswordLabel')}
              </label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder={t('accountExtra.enterNewPassword')}
                disabled={changingPassword}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                {t('accountExtra.confirmNewPasswordLabel')}
              </label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder={t('accountExtra.reenterNewPassword')}
                disabled={changingPassword}
              />
            </div>
            
            <div className="password-form-actions" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button 
                className="btn btnSecondaryOrange" 
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
                }}
                disabled={changingPassword}
              >
                {t('accountExtra.cancelBtn')}
              </button>
              <button 
                className="btn btnPrimaryOrange" 
                onClick={handleChangePassword}
                disabled={changingPassword}
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="animate-spin" style={{ marginRight: '0.5rem' }} size={16} />
                    {t('accountExtra.changingMsg')}
                  </>
                ) : (
                  t('accountExtra.changePasswordMenu')
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Email Dialog (NEW) */}
      <Dialog open={showUpdateEmail} onOpenChange={setShowUpdateEmail}>
        <DialogContent className="update-email-modal">
          <DialogHeader>
            <DialogTitle>{t('account.updateEmail')}</DialogTitle>
          </DialogHeader>
          <div className="email-form-content" style={{ marginTop: '1.5rem' }}>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                {t('account.currentPassword')}
              </label>
              <Input
                type="password"
                value={emailForm.currentPassword}
                onChange={(e) => setEmailForm({ ...emailForm, currentPassword: e.target.value })}
                placeholder={t('accountExtra.enterCurrentPassword')}
                disabled={updatingEmail}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                {t('account.newEmailAddress')}
              </label>
              <Input
                type="email"
                value={emailForm.newEmail}
                onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                placeholder={t('accountExtra.enterNewEmail')}
                disabled={updatingEmail}
              />
              <p style={{ fontSize: '12px', color: '#666', marginTop: '0.5rem' }}>
                {t('acco.currentEmail')}
                <strong>{user?.email}</strong>
              </p>
            </div>
            
            <div style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', padding: '0.75rem', marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '12px', color: '#92400E', lineHeight: '1.5' }}>
                <strong>{t('accountExtra.updateEmailNoteLabel')}</strong>{' '}
                {t('accountExtra.updateEmailNote')}
              </p>
            </div>
            
            <div className="email-form-actions" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button 
                className="btn btnSecondaryOrange" 
                onClick={() => {
                  setShowUpdateEmail(false);
                  setEmailForm({ currentPassword: '', newEmail: '' });
                }}
                disabled={updatingEmail}
              >
                {t('account.cancel')}
              </button>
              <button 
                className="btn btnPrimaryOrange" 
                onClick={handleUpdateEmail}
                disabled={updatingEmail}
              >
                {updatingEmail ? (
                  <>
                    <Loader2 className="animate-spin" style={{ marginRight: '0.5rem' }} size={16} />
                    {t('account.saving')}
                  </>
                ) : (
                  t('account.updateEmail')
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Clear Cache Confirmation Dialog */}
      <AlertDialog open={showClearCacheDialog} onOpenChange={setShowClearCacheDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('account.clearAppCache')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('account.clearCacheConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel className="btn btnSecondaryOrange" disabled={clearingCache} data-testid="clear-cache-cancel-btn">{t('account.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleClearCache(); }}
              className="btn btnPrimaryOrange"
              disabled={clearingCache}
              data-testid="clear-cache-confirm-btn"
            >
              {clearingCache ? t('account.saving') : t('accountExtra.clearCacheBtn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* NEW: Notification Settings Dialog */}
      <Dialog open={showNotificationSettings} onOpenChange={setShowNotificationSettings}>
        <DialogContent className="notification-settings-modal">
          <DialogHeader>
            <DialogTitle>{t('account.notificationSettings')}</DialogTitle>
          </DialogHeader>
          <div className="notification-settings-content" style={{ marginTop: '1rem' }} data-testid="notification-settings-content">
            {/* SECTION A — delivery channels */}
            <div className="notif-section">
              <p className="notif-section-title">{t('account.notifChannelsSection')}</p>
              <div className="menu-item-toggle" style={{ marginBottom: '0.75rem' }}>
                <div className="menu-item-left">
                  <Bell size={20} />
                  <span>{t('account.pushNotifications')}</span>
                </div>
                <Switch 
                  data-testid="notif-push-toggle"
                  checked={notificationPrefs.push_enabled} 
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, push_enabled: checked })} 
                />
              </div>
              <div className="menu-item-toggle">
                <div className="menu-item-left">
                  <Mail size={20} />
                  <span>{t('account.emailNotifications')}</span>
                </div>
                <Switch 
                  data-testid="notif-email-toggle"
                  checked={notificationPrefs.email_enabled} 
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, email_enabled: checked })} 
                />
              </div>
            </div>

            {/* SECTION B — notification types */}
            <div className="notif-section">
              <p className="notif-section-title">{t('account.notifTypesSection')}</p>
              <div className="menu-item-toggle" style={{ marginBottom: '0.75rem' }}>
                <div className="menu-item-left">
                  <MessageSquare size={20} />
                  <span>{t('account.messages')}</span>
                </div>
                <Switch 
                  data-testid="notif-messages-toggle"
                  checked={notificationPrefs.messages} 
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, messages: checked })} 
                />
              </div>
              <div className="menu-item-toggle" style={{ marginBottom: '0.75rem' }}>
                <div className="menu-item-left">
                  <Package size={20} />
                  <span>{t('account.listingActivity')}</span>
                </div>
                <Switch 
                  data-testid="notif-listing-toggle"
                  checked={notificationPrefs.listing_activity} 
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, listing_activity: checked })} 
                />
              </div>
              <div className="menu-item-toggle">
                <div className="menu-item-left">
                  <Bookmark size={20} />
                  <span>{t('account.savedSearchesNotif')}</span>
                </div>
                <Switch 
                  data-testid="notif-saved-searches-toggle"
                  checked={notificationPrefs.saved_searches} 
                  onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, saved_searches: checked })} 
                />
              </div>
            </div>

            {/* Essential alerts — always on, no switch */}
            <div className="notif-section">
              <div className="notif-essential" data-testid="notif-essential-row">
                <ShieldCheck size={20} className="notif-essential-icon" />
                <div>
                  <p className="notif-essential-title">{t('account.essentialAlerts')}</p>
                  <p className="notif-essential-desc">{t('account.essentialAlertsDesc')}</p>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button 
                className="btn btnPrimaryOrange" 
                onClick={handleSaveNotificationPrefs}
                disabled={savingPrefs}
                data-testid="notif-save-btn"
              >
                {savingPrefs ? t('account.saving') : t('account.saveChanges')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="role-dialog-content">
          <RoleSelection 
            currentRole={userRole} 
            onComplete={(role) => {
              setRoleDialogOpen(false);
              // Profile page will auto-refresh via updateUser in RoleSelection
            }}
            onBack={() => setRoleDialogOpen(false)}
            showBackButton={false}
            isModal={true}
          />
        </DialogContent>
      </Dialog>

      {/* Recall Confirmation Dialog */}
      <ConfirmDialog
        open={recallConfirm.open}
        title={t('accountExtra.recallAdTitle')}
        message={t('accountExtra.recallAdMsg', { title: recallConfirm.adTitle })}
        confirmText={t('accountExtra.yesRecall')}
        cancelText={t('accountExtra.cancelBtn')}
        danger
        loading={recallLoading}
        onConfirm={executeRecallAd}
        onCancel={cancelRecallAd}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('accountExtra.deleteAdTitle')}
        message={t('accountExtra.deleteAdMsg', { title: deleteConfirm.adTitle })}
        confirmText={t('account.delete')}
        cancelText={t('accountExtra.cancelBtn')}
        danger
        loading={deleteLoading}
        onConfirm={executeDeleteAd}
        onCancel={cancelDeleteAd}
      />

      {/* Social Link Modal */}
      <Dialog open={socialLinkModal.open} onOpenChange={(open) => !open && closeSocialLinkModal()}>
        <DialogContent className="social-link-modal">
          <DialogHeader>
            <DialogTitle>
              {socialLinkModal.provider === 'facebook' 
                ? (t('accountExtra.linkFacebook'))
                : (t('accountExtra.linkInstagram'))
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="social-link-form">
            <div className="social-link-icon-wrapper">
              {socialLinkModal.provider === 'facebook' ? (
                <div className="social-link-icon facebook">
                  <Facebook size={32} />
                </div>
              ) : (
                <div className="social-link-icon instagram">
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </div>
              )}
            </div>
            
            <p className="social-link-description">
              {socialLinkModal.provider === 'facebook'
                ? (t('accountExtra.facebookDesc'))
                : (t('accountExtra.instagramDesc'))
              }
            </p>
            
            <div className="social-link-input-wrapper">
              <Input
                type="text"
                value={socialLinkModal.inputValue}
                onChange={(e) => setSocialLinkModal(prev => ({ 
                  ...prev, 
                  inputValue: e.target.value,
                  error: null 
                }))}
                placeholder={
                  socialLinkModal.provider === 'facebook'
                    ? (t('accountExtra.facebookPlaceholder'))
                    : (t('accountExtra.instagramPlaceholder'))
                }
                disabled={socialLinkModal.saving}
                className={socialLinkModal.error ? 'input-error' : ''}
                data-testid="social-link-input"
              />
              {socialLinkModal.error && (
                <p className="social-link-error" data-testid="social-link-error">{socialLinkModal.error}</p>
              )}
            </div>
            
            <div className="social-link-actions">
              <button 
                className="btn btnSecondaryOrange"
                onClick={closeSocialLinkModal}
                disabled={socialLinkModal.saving}
                data-testid="social-link-cancel-btn"
              >
                {t('accountExtra.cancelBtn')}
              </button>
              <button 
                className="btn btnPrimaryGreen"
                onClick={handleSocialLinkSave}
                disabled={socialLinkModal.saving || !socialLinkModal.inputValue.trim()}
                data-testid="social-link-save-btn"
              >
                {socialLinkModal.saving ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    {t('accountExtra.linkingMsg')}
                  </>
                ) : (
                  t('accountExtra.linkAccountBtn')
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unlink Confirmation Modal */}
      <AlertDialog open={confirmUnlink.open} onOpenChange={(open) => !open && handleCancelUnlink()}>
        <AlertDialogContent className="unlink-confirm-modal" dir={isRTL ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle className="unlink-confirm-title">
              {t('accountExtra.unlinkAccountTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="unlink-confirm-description">
              {t('accountExtra.unlinkDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={`unlink-confirm-footer ${isRTL ? 'rtl' : ''}`}>
            <AlertDialogCancel 
              onClick={handleCancelUnlink}
              disabled={confirmUnlink.unlinking}
              className="unlink-cancel-btn"
            >
              {t('accountExtra.cancelBtn')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmUnlink}
              disabled={confirmUnlink.unlinking}
              className="unlink-confirm-btn"
            >
              {confirmUnlink.unlinking ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  {t('accountExtra.unlinkingMsg')}
                </>
              ) : (
                t('accountExtra.unlinkConfirmBtn')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Profile Type Picker */}
      <ProfileTypePicker
        isOpen={showProfileTypePicker}
        onClose={() => setShowProfileTypePicker(false)}
        onSelect={handleProfileTypeSelect}
        lang={isRTL ? 'ar' : 'en'}
      />
      
      {/* Boost Selection Modal */}
      <BoostSelectionModal
        open={boostModalOpen}
        onClose={() => {
          setBoostModalOpen(false);
          setSelectedAdForBoost(null);
        }}
        ad={selectedAdForBoost}
        userCoins={user?.coins || 0}
        onBoostSuccess={handleBoostSuccess}
      />
      
      {/* About Wassla Modal */}
      <AboutWasslaModal
        isOpen={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />
      
      {/* Draft Resume Modal */}
      {DraftResumeModal}
    </div>
  );
}

export default Account;
